import React, { useState, useEffect } from 'react';
import './ContactPage.css';
import contactBg from '../../assets/images/contact.svg';
import { contactInfoPublicApi, inquiriesPublicApi } from '../../api/adminApi';

const DEFAULT_CONTACT = {
  address: '72 Clarkson Avenue, Brooklyn, New York 11226',
  phone: '718-462-0776',
  email: 'davidfosterpreschool@verizon.net',
  hours: 'Monday - Friday: 8:00 AM - 7:00 PM',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateForm(data) {
  const errors = {};
  if (!data.parentName?.trim()) {
    errors.parentName = 'Parent name is required.';
  } else if (data.parentName.trim().length < 2) {
    errors.parentName = 'Parent name must be at least 2 characters.';
  }
  if (!data.childName?.trim()) {
    errors.childName = "Child's name is required.";
  } else if (data.childName.trim().length < 2) {
    errors.childName = "Child's name must be at least 2 characters.";
  }
  if (!data.childAge?.trim()) {
    errors.childAge = "Child's age is required.";
  } else {
    const age = parseInt(data.childAge, 10);
    if (Number.isNaN(age) || age < 0 || age > 18) {
      errors.childAge = 'Child age must be between 0 and 18.';
    }
  }
  if (!data.phone?.trim()) {
    errors.phone = 'Phone number is required.';
  } else {
    const digits = data.phone.replace(/\D/g, '');
    if (digits.length < 10) {
      errors.phone = 'Please enter a valid phone number (at least 10 digits).';
    }
  }
  if (!data.email?.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!data.message?.trim()) {
    errors.message = 'Message is required.';
  } else if (data.message.trim().length > 2000) {
    errors.message = 'Message must be 2000 characters or less.';
  }
  return errors;
}

function ContactPage() {
  const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT);
  const [contactLoading, setContactLoading] = useState(true);
  const [formData, setFormData] = useState({
    parentName: '',
    childName: '',
    childAge: '',
    phone: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    contactInfoPublicApi
      .get()
      .then((data) => {
        if (data?.address || data?.phone || data?.email || data?.hours) {
          setContactInfo((prev) => ({
            ...prev,
            ...data,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setContactLoading(false));
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitStatus('loading');
    setSubmitError('');
    inquiriesPublicApi
      .create({
        parent_name: formData.parentName,
        child_name: formData.childName,
        child_age: formData.childAge,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
      })
      .then(() => {
        setSubmitStatus('success');
        setFormData({ parentName: '', childName: '', childAge: '', phone: '', email: '', message: '' });
      })
      .catch((err) => {
        setSubmitStatus('error');
        setSubmitError(err?.userMessage || err?.message || 'Failed to submit inquiry. Please try again.');
      });
  };

  return (
    <main className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero" aria-label="Contact us">
        <div className="contact-hero-bg" style={{ backgroundImage: `url(${contactBg})` }} aria-hidden="true" />
        {/* <div className="contact-hero-overlay" aria-hidden="true" /> */}
        {/* <div className="contact-hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="#F8F8F8" d="M0 80V40c120 20 240 40 360 40s240-20 360-40 240-40 360-40 240 20 360 40v40H0z" />
          </svg>
        </div> */}
        <div className="contact-hero-inner">
          <h1 className="contact-hero-title">We&apos;d Love to Hear From You</h1>
          <p className="contact-hero-desc">
            Have questions? Ready to schedule a tour? We&apos;re here to help you take the first step toward your child&apos;s bright future.
          </p>
        </div>
      </section>

      {/* Form & Contact Info */}
      <section className="contact-content" aria-label="Contact form and information">
        <div className="contact-content-inner">
          <div className="contact-form-wrap">
            <div className="contact-form-card">
              <h2 className="contact-form-title">Send Us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="contact-form-row">
                  <div className="contact-form-field">
                    <label htmlFor="parentName">Parent Name *</label>
                    <input
                      id="parentName"
                      name="parentName"
                      type="text"
                      value={formData.parentName}
                      onChange={handleFormChange}
                      className={errors.parentName ? 'contact-form-input-invalid' : ''}
                      aria-invalid={!!errors.parentName}
                      aria-describedby={errors.parentName ? 'parentName-error' : undefined}
                    />
                    {errors.parentName && (
                      <span id="parentName-error" className="contact-form-field-error" role="alert">
                        {errors.parentName}
                      </span>
                    )}
                  </div>
                  <div className="contact-form-field">
                    <label htmlFor="childName">Child&apos;s Name *</label>
                    <input
                      id="childName"
                      name="childName"
                      type="text"
                      value={formData.childName}
                      onChange={handleFormChange}
                      className={errors.childName ? 'contact-form-input-invalid' : ''}
                      aria-invalid={!!errors.childName}
                      aria-describedby={errors.childName ? 'childName-error' : undefined}
                    />
                    {errors.childName && (
                      <span id="childName-error" className="contact-form-field-error" role="alert">
                        {errors.childName}
                      </span>
                    )}
                  </div>
                </div>
                <div className="contact-form-row">
                  <div className="contact-form-field">
                    <label htmlFor="childAge">Child&apos;s Age *</label>
                    <input
                      id="childAge"
                      name="childAge"
                      type="text"
                      inputMode="numeric"
                      value={formData.childAge}
                      onChange={handleFormChange}
                      className={errors.childAge ? 'contact-form-input-invalid' : ''}
                      aria-invalid={!!errors.childAge}
                      aria-describedby={errors.childAge ? 'childAge-error' : undefined}
                    />
                    {errors.childAge && (
                      <span id="childAge-error" className="contact-form-field-error" role="alert">
                        {errors.childAge}
                      </span>
                    )}
                  </div>
                  <div className="contact-form-field">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleFormChange}
                      className={errors.phone ? 'contact-form-input-invalid' : ''}
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                    />
                    {errors.phone && (
                      <span id="phone-error" className="contact-form-field-error" role="alert">
                        {errors.phone}
                      </span>
                    )}
                  </div>
                </div>
                <div className="contact-form-field">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className={errors.email ? 'contact-form-input-invalid' : ''}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <span id="email-error" className="contact-form-field-error" role="alert">
                      {errors.email}
                    </span>
                  )}
                </div>
                <div className="contact-form-field">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleFormChange}
                    className={errors.message ? 'contact-form-input-invalid' : ''}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                  />
                  {errors.message && (
                    <span id="message-error" className="contact-form-field-error" role="alert">
                      {errors.message}
                    </span>
                  )}
                </div>
                {submitStatus === 'success' && (
                  <p className="contact-form-success" role="alert">
                    Thank you! Your inquiry has been submitted successfully.
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="contact-form-error" role="alert">
                    {submitError}
                  </p>
                )}
                <button
                  type="submit"
                  className="contact-form-submit"
                  disabled={submitStatus === 'loading'}
                >
                  {submitStatus === 'loading' ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>

          <div className="contact-info">
            <h2 className="contact-info-title">Contact Information</h2>
            {contactLoading ? (
              <p className="contact-info-loading">Loading...</p>
            ) : (
              <ul className="contact-info-list">
                <li className="contact-info-item">
                  <span className="contact-info-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="white"/>
                    </svg>
                  </span>
                  <div>
                    <strong>Address</strong>
                    <p>{contactInfo.address}</p>
                  </div>
                </li>
                <li className="contact-info-item">
                  <span className="contact-info-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="white"/>
                    </svg>
                  </span>
                  <div>
                    <strong>Phone</strong>
                    <p><a href={`tel:${contactInfo.phone?.replace(/\D/g, '')}`}>{contactInfo.phone}</a></p>
                  </div>
                </li>
                <li className="contact-info-item">
                  <span className="contact-info-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="white"/>
                    </svg>
                  </span>
                  <div>
                    <strong>Email</strong>
                    <p><a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a></p>
                  </div>
                </li>
                <li className="contact-info-item">
                  <span className="contact-info-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="white"/>
                    </svg>
                  </span>
                  <div>
                    <strong>Hours</strong>
                    <p>{contactInfo.hours}</p>
                  </div>
                </li>
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="contact-map" aria-label="Location map">
        <iframe
          title="David Foster Preschool Location"
          src={`https://www.google.com/maps?q=${encodeURIComponent(contactInfo.address || '72 Clarkson Avenue Brooklyn NY 11226')}&output=embed`}
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </main>
  );
}

export default ContactPage;
